/**
 * Карточка diff одной сущности: collapsible + CodeMirror MergeView
 * (side-by-side построчный diff DSL-исходника).
 *
 * Контракт `EntityDiff`:
 *   - side='modified' → отдаём sourceBefore/sourceAfter в MergeView
 *   - side='added'    → MergeView с пустым base слева
 *   - side='removed'  → MergeView с пустым head справа
 *
 * MergeView создаётся один раз при mount, destroy при unmount. Re-render
 * при изменении props игнорируем — карточки в списке стабильны
 * (key по type+name+canvas, при изменении пара полностью пере-mount'ится).
 */

import { Accordion, Badge, Box, Group, Text } from '@mantine/core';
import { MergeView } from '@codemirror/merge';
import { EditorState } from '@codemirror/state';
import { EditorView, lineNumbers, highlightActiveLine } from '@codemirror/view';
import { syntaxHighlighting, defaultHighlightStyle } from '@codemirror/language';
import { useEffect, useRef } from 'react';
import type { EntityDiff } from '@pflow/shared/v2/diff';

interface Props {
    diff: EntityDiff;
}

const SIDE_COLOR: Record<EntityDiff['side'], string> = {
    added:    'green',
    removed:  'red',
    modified: 'yellow',
};

const SIDE_LABEL: Record<EntityDiff['side'], string> = {
    added:    'добавлена',
    removed:  'удалена',
    modified: 'изменена',
};

export function EntityDiffCard({ diff }: Props): JSX.Element {
    const itemKey = `${diff.entity.type}::${diff.entity.name}::${diff.entity.canvas ?? ''}`;
    const hasNarrative = !!diff.narrative && diff.narrative !== 'минор';
    return (
        <Accordion.Item value={itemKey}>
            <Accordion.Control>
                <Group justify="space-between" wrap="nowrap" align="flex-start">
                    <Box style={{ minWidth: 0 }}>
                        <Group gap="xs">
                            <Text fz="xs" c="dimmed">{diff.entity.type}</Text>
                            <Text fw={600}>«{diff.entity.name}»</Text>
                            {diff.entity.canvas && (
                                <Text fz="xs" c="dimmed">в Канвасе «{diff.entity.canvas}»</Text>
                            )}
                        </Group>
                        {hasNarrative && (
                            <Text fz="sm" mt={4} style={{ whiteSpace: 'pre-wrap' }}>
                                {diff.narrative}
                            </Text>
                        )}
                        <Text fz="xs" c="dimmed" mt={hasNarrative ? 4 : 0}>{diff.file}</Text>
                    </Box>
                    <Badge color={SIDE_COLOR[diff.side]} variant="light" size="sm">
                        {SIDE_LABEL[diff.side]}
                    </Badge>
                </Group>
            </Accordion.Control>
            <Accordion.Panel>
                <DiffView before={diff.sourceBefore ?? ''} after={diff.sourceAfter ?? ''} />
            </Accordion.Panel>
        </Accordion.Item>
    );
}

export function DiffView({ before, after }: { before: string; after: string }): JSX.Element {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const viewRef      = useRef<MergeView | null>(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const extensions = [
            lineNumbers(),
            highlightActiveLine(),
            EditorView.editable.of(false),
            EditorState.readOnly.of(true),
            syntaxHighlighting(defaultHighlightStyle),
            EditorView.theme({
                '&': {
                    fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
                    fontSize:   '13px',
                },
                '.cm-content': { padding: '6px 0' },
                '.cm-scroller': { lineHeight: '1.5' },
            }),
        ];

        viewRef.current = new MergeView({
            parent:   containerRef.current,
            a: { doc: before, extensions },
            b: { doc: after,  extensions },
            // collapseUnchanged скрывает крупные неизменённые куски,
            // оставляя ±2 строки контекста. Для блоков сущностей
            // обычно весь блок небольшой; не сворачиваем.
            revertControls: undefined,
            highlightChanges: true,
            gutter: true,
        });

        return () => {
            viewRef.current?.destroy();
            viewRef.current = null;
        };
    }, [before, after]);

    return (
        <Box
            ref={containerRef}
            style={{
                border:       '1px solid var(--mantine-color-default-border)',
                borderRadius: 4,
                overflow:     'auto',
                maxHeight:    '600px',
            }}
        />
    );
}
